/**
 * Ranking de vendas (#557): quem vendeu mais no período.
 *
 * ── Pódio para a TV, lista para conferir ──────────────────────────────────
 *
 * Os três primeiros ganham destaque com foto, porque a tela também fica aberta
 * numa TV da gestão e o pódio é o que se lê de longe. Do quarto em diante a
 * lista traz o que o gestor confere de perto: ticket médio e se a pessoa subiu
 * ou desceu em relação ao período anterior.
 *
 * ── O pódio se revela ─────────────────────────────────────────────────────
 *
 * Do terceiro ao primeiro: o degrau sobe, a pessoa aparece, e por último a
 * coroa cai sobre o primeiro lugar. A revelação roda quando o PÓDIO muda — ao
 * abrir a tela, ou quando um filtro troca quem está nele —, e não a cada
 * filtro: quem confere números trocando de funil não quer assistir à mesma
 * cerimônia de novo. "Rever a revelação" roda de novo, para a TV. Os tempos
 * são múltiplos de `--transicao-entrada`, então quem pediu menos movimento vê
 * o pódio já montado.
 *
 * ── Todo número leva às vendas que o formaram ─────────────────────────────
 *
 * Clicar numa pessoa abre as vendas dela, pela mesma regra e com os mesmos
 * filtros (a regra do painel: detalhamento que cai num conjunto diferente é
 * pior que nenhum).
 *
 * ── A falta de dado é dita, e não escondida ───────────────────────────────
 *
 * Venda sem valor conta na quantidade e soma zero; a tela diz quantas são. As
 * vendas sem responsável não têm pódio, e também são ditas.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { PosicaoNoRanking, RankingDeVendas } from '@cliqcrm/contracts';
import { useRankingDeVendas, useVendasDoRanking } from '@/shared/api/consultas';
import { Estado } from '@/shared/ui/estados/estados';
import { Avatar, CabecalhoDeTela, CampoSelecao, Dialogo } from '@/shared/ui';
import { diaPorExtenso, diasDoPeriodo, PERIODO_PADRAO, type ChaveDePeriodo } from '../lib/periodo';
import { BarraDoRelatorio, TituloDoCartao } from './barra';
import { ABRANGENCIA, dinheiro } from '../lib/formato';
import { BotaoExportar } from './exportar';
import { nomeDaPlanilha, planilhaDoRanking } from '../lib/planilhas';
import './relatorios.css';

const vendasEmPalavras = (n: number): string => (n === 1 ? '1 venda' : `${n} vendas`);

export function RankingDeVendasTela(): ReactNode {
  const [periodo, definirPeriodo] = useState<ChaveDePeriodo>(PERIODO_PADRAO);
  const [funil, definirFunil] = useState('');
  const [time, definirTime] = useState('');
  const [vendo, definirVendo] = useState<PosicaoNoRanking | null>(null);

  const filtro = {
    dias: String(diasDoPeriodo(periodo)),
    ...(funil ? { funil } : {}),
    ...(time ? { time } : {}),
  };
  const consulta = useRankingDeVendas(filtro);

  /*
   * As opções dos filtros ficam guardadas da última resposta boa.
   *
   * Os filtros moram FORA da área que carrega: dentro dela, trocar de funil
   * fazia o campo sumir debaixo do cursor enquanto a resposta nova não vinha,
   * e um erro com filtro aplicado escondia justamente o controle que o desfaz.
   */
  const [opcoes, definirOpcoes] = useState<RankingDeVendas['filtros'] | null>(null);
  useEffect(() => {
    if (consulta.data) definirOpcoes(consulta.data.filtros);
  }, [consulta.data]);

  // A equipe escolhida deixou de existir (ou de estar na carteira): volta para todas.
  const codigoDoErro = (consulta.error as { codigo?: string } | null)?.codigo;
  useEffect(() => {
    if (time && codigoDoErro === 'NAO_ENCONTRADO') definirTime('');
  }, [time, codigoDoErro]);

  return (
    <div className="relatorio">
      <CabecalhoDeTela
        titulo="Ranking de vendas"
        apoio="Quem mais vendeu no período, pelo valor vendido."
        acoes={
          <BotaoExportar
            planilha={
              consulta.data
                ? () => ({
                    conteudo: planilhaDoRanking(consulta.data!, {
                      funil: opcoes?.funis.find((f) => f.chave === funil)?.nome,
                      time: opcoes?.times.find((t) => t.id === time)?.nome,
                    }),
                    nome: nomeDaPlanilha('ranking', consulta.data!.periodo),
                  })
                : null
            }
          />
        }
        {...(consulta.data
          ? {
              meta: (
                <span>
                  {diaPorExtenso(consulta.data.periodo.de)} a{' '}
                  {diaPorExtenso(consulta.data.periodo.ate)}
                </span>
              ),
            }
          : {})}
        extra={
          <BarraDoRelatorio
            periodo={periodo}
            aoEscolherPeriodo={definirPeriodo}
            {...(consulta.data ? { abrangencia: ABRANGENCIA[consulta.data.abrangencia] } : {})}
          >
            {opcoes && (
              <Filtros
                opcoes={opcoes}
                funil={funil}
                time={time}
                aoEscolherFunil={definirFunil}
                aoEscolherTime={definirTime}
              />
            )}
          </BarraDoRelatorio>
        }
      />

      <div className="relatorio__corpo">
        <Estado consulta={consulta} forma="cartoes" linhas={3}>
          {(dados) => (
            <>
              {dados.itens.length > 0 && <Total dados={dados} />}
              <Avisos dados={dados} />
              {dados.itens.length === 0 ? (
                <p className="relatorio__vazio">Nenhuma venda no período.</p>
              ) : (
                <>
                  <Podio dados={dados} aoAbrir={definirVendo} />
                  <Lista dados={dados} aoAbrir={definirVendo} />
                </>
              )}
              {dados.sem_vendas.length > 0 && <SemVendas dados={dados} />}
            </>
          )}
        </Estado>
      </div>

      {vendo && consulta.data && (
        <VendasDaPessoa
          pessoa={vendo}
          moeda={consulta.data.moeda}
          // As DATAS que o ranking usou, e não "os últimos N dias" de novo: virando
          // a meia-noite entre abrir o ranking e clicar, o detalhe cairia noutro
          // período e não bateria com o número.
          filtro={{
            de: consulta.data.periodo.de,
            ate: consulta.data.periodo.ate,
            ...(funil ? { funil } : {}),
          }}
          aoFechar={() => definirVendo(null)}
        />
      )}
    </div>
  );
}

function Filtros({
  opcoes,
  funil,
  time,
  aoEscolherFunil,
  aoEscolherTime,
}: {
  opcoes: RankingDeVendas['filtros'];
  funil: string;
  time: string;
  aoEscolherFunil: (valor: string) => void;
  aoEscolherTime: (valor: string) => void;
}): ReactNode {
  const { funis, times } = opcoes;
  return (
    <>
      {funis.length >= 2 && (
        <span className="relatorio__filtro">
          <CampoSelecao
            rotulo="Funil"
            tamanho="miudo"
            ativo={funil !== ''}
            value={funil}
            onChange={(evento) => aoEscolherFunil(evento.target.value)}
          >
            <option value="">Todos os funis</option>
            {funis.map((f) => (
              <option key={f.chave} value={f.chave}>
                {f.nome}
              </option>
            ))}
          </CampoSelecao>
        </span>
      )}
      {times.length > 0 && (
        <span className="relatorio__filtro">
          <CampoSelecao
            rotulo="Equipe"
            tamanho="miudo"
            ativo={time !== ''}
            value={time}
            onChange={(evento) => aoEscolherTime(evento.target.value)}
          >
            <option value="">Todas as equipes</option>
            {times.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </CampoSelecao>
        </span>
      )}
    </>
  );
}

/** O total de quem está no ranking. Vendas fora dele são ditas nos avisos, logo abaixo. */
function Total({ dados }: { dados: RankingDeVendas }): ReactNode {
  const valor = dados.itens.reduce((soma, p) => soma + p.valor_micros, 0);
  const vendas = dados.itens.reduce((soma, p) => soma + p.quantidade, 0);
  const pessoas = dados.itens.length;
  return (
    <p className="ranking-total">
      <span className="ranking-total__valor">{dinheiro(valor, dados.moeda)}</span>
      <span className="ranking-total__apoio">
        em <strong>{vendasEmPalavras(vendas)}</strong> ·{' '}
        {pessoas === 1 ? '1 pessoa vendeu' : `${pessoas} pessoas venderam`}
      </span>
    </p>
  );
}

function Avisos({ dados }: { dados: RankingDeVendas }): ReactNode {
  const avisos: string[] = [];
  if (dados.vendas_sem_valor > 0) {
    avisos.push(
      `${vendasEmPalavras(dados.vendas_sem_valor)} do período sem valor preenchido: contam na quantidade e somam zero.`,
    );
  }
  if (dados.vendas_sem_oportunidade > 0) {
    avisos.push(
      `${vendasEmPalavras(dados.vendas_sem_oportunidade)} de oportunidades apagadas ou ainda não sincronizadas, fora do ranking.`,
    );
  }
  if (dados.sem_responsavel.quantidade > 0) {
    avisos.push(
      `${vendasEmPalavras(dados.sem_responsavel.quantidade)} sem responsável, somando ${dinheiro(
        dados.sem_responsavel.valor_micros,
        dados.moeda,
      )}, fora do ranking.`,
    );
  }
  if (avisos.length === 0) return null;
  return (
    <ul className="relatorio__avisos">
      {avisos.map((aviso) => (
        <li key={aviso}>{aviso}</li>
      ))}
    </ul>
  );
}

/**
 * Os três primeiros, na ordem do pódio: segundo, primeiro, terceiro.
 *
 * A ordem do DOM é a da posição (1, 2, 3), para leitor de tela e teclado; quem
 * desenha o pódio é o CSS (`order`). A `key` do `<ol>` é quem está no pódio:
 * mudou alguém, ele remonta e a revelação roda de novo.
 */
function Podio({
  dados,
  aoAbrir,
}: {
  dados: RankingDeVendas;
  aoAbrir: (pessoa: PosicaoNoRanking) => void;
}): ReactNode {
  const [rodada, definirRodada] = useState(0);
  const tres = dados.itens.slice(0, 3);
  const quem = tres.map((p) => p.responsavel_referencia_externa).join('|');

  return (
    <div className="podio__palco">
      <ol key={`${quem}#${rodada}`} className="podio" aria-label="Pódio">
        {tres.map((pessoa) => (
          <li
            key={pessoa.responsavel_referencia_externa}
            className={`podio__lugar podio__lugar--${pessoa.posicao}`}
          >
            <button
              type="button"
              className="podio__botao"
              onClick={() => aoAbrir(pessoa)}
              aria-label={`${pessoa.posicao}º lugar, ${pessoa.nome ?? 'Responsável sem nome'}: ${dinheiro(
                pessoa.valor_micros,
                dados.moeda,
              )} em ${vendasEmPalavras(pessoa.quantidade)}. Ver as vendas`}
            >
              <span className="podio__retrato">
                {pessoa.posicao === 1 && <Coroa />}
                <Avatar
                  nome={pessoa.nome ?? '?'}
                  tamanho={pessoa.posicao === 1 ? 56 : 48}
                  foto={pessoa.foto_url}
                  cheio={pessoa.posicao === 1}
                />
              </span>
              <span className="podio__nome">{pessoa.nome ?? 'Responsável sem nome'}</span>
              <span className="podio__valor">{dinheiro(pessoa.valor_micros, dados.moeda)}</span>
              <span className="podio__quantidade">{vendasEmPalavras(pessoa.quantidade)}</span>
            </button>
            <span className="podio__degrau" aria-hidden="true">
              {pessoa.posicao}º
            </span>
          </li>
        ))}
      </ol>
      <button type="button" className="podio__rever" onClick={() => definirRodada((n) => n + 1)}>
        Rever a revelação do pódio
      </button>
    </div>
  );
}

/** A coroa do primeiro lugar e as quatro faíscas que a acompanham. Só desenho. */
function Coroa(): ReactNode {
  return (
    <span className="coroa" aria-hidden="true">
      <svg className="coroa__desenho" width="44" height="32" viewBox="0 0 44 32">
        <path
          className="coroa__corpo"
          d="M4 26 L2 8 L13 16 L22 3 L31 16 L42 8 L40 26 Z"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <rect className="coroa__base" x="4" y="26" width="36" height="4" rx="2" />
        <circle className="coroa__joia" cx="22" cy="18" r="2.6" />
        <circle className="coroa__ponta" cx="2" cy="8" r="2.4" />
        <circle className="coroa__ponta" cx="22" cy="3" r="2.4" />
        <circle className="coroa__ponta" cx="42" cy="8" r="2.4" />
      </svg>
      {[1, 2, 3, 4].map((n) => (
        <svg
          key={n}
          className={`coroa__faisca coroa__faisca--${n}`}
          width="14"
          height="14"
          viewBox="0 0 14 14"
        >
          <path d="M7 0 L8.6 5.4 L14 7 L8.6 8.6 L7 14 L5.4 8.6 L0 7 L5.4 5.4 Z" />
        </svg>
      ))}
    </span>
  );
}

function Variacao({ pessoa }: { pessoa: PosicaoNoRanking }): ReactNode {
  if (pessoa.posicao_anterior === null) {
    return <span className="variacao variacao--nova">sem vendas antes</span>;
  }
  const diferenca = pessoa.posicao_anterior - pessoa.posicao;
  if (diferenca === 0) return <span className="variacao">mesma posição</span>;
  return diferenca > 0 ? (
    <span className="variacao variacao--subiu">
      <span aria-hidden="true">▲ </span>
      <span>subiu {diferenca}</span>
    </span>
  ) : (
    <span className="variacao variacao--desceu">
      <span aria-hidden="true">▼ </span>
      <span>desceu {-diferenca}</span>
    </span>
  );
}

function Lista({
  dados,
  aoAbrir,
}: {
  dados: RankingDeVendas;
  aoAbrir: (pessoa: PosicaoNoRanking) => void;
}): ReactNode {
  const maior = Math.max(1, ...dados.itens.map((p) => p.valor_micros));
  return (
    <section className="relatorio__cartao" aria-labelledby="ranking-todas">
      <TituloDoCartao id="ranking-todas">Todas as posições</TituloDoCartao>
      <div className="relatorio__tabela relatorio__tabela--solta">
        <table>
          <caption className="visualmente-oculto">Todas as posições</caption>
          <thead>
            <tr>
              <th scope="col" className="relatorio__num">
                Posição
              </th>
              <th scope="col">Pessoa</th>
              <th scope="col" className="relatorio__num">
                Valor vendido
              </th>
              <th scope="col" className="relatorio__num">
                Vendas
              </th>
              <th scope="col" className="relatorio__num">
                Ticket médio
              </th>
              <th scope="col">Período anterior</th>
            </tr>
          </thead>
          <tbody>
            {dados.itens.map((pessoa) => (
              <tr key={pessoa.responsavel_referencia_externa}>
                <td className="relatorio__num relatorio__posicao">{pessoa.posicao}º</td>
                <td>
                  <button
                    type="button"
                    className="relatorio__pessoa"
                    onClick={() => aoAbrir(pessoa)}
                  >
                    <Avatar nome={pessoa.nome ?? '?'} tamanho={28} foto={pessoa.foto_url} />
                    <span>{pessoa.nome ?? 'Responsável sem nome'}</span>
                  </button>
                </td>
                <td className="relatorio__num">
                  <span className="ranking-valor">
                    <span className="ranking-valor__trilho" aria-hidden="true">
                      <span
                        className={
                          pessoa.posicao <= 3
                            ? 'ranking-valor__barra ranking-valor__barra--podio'
                            : 'ranking-valor__barra'
                        }
                        style={{ width: `${(pessoa.valor_micros / maior) * 100}%` }}
                      />
                    </span>
                    <span className="ranking-valor__texto">
                      {dinheiro(pessoa.valor_micros, dados.moeda)}
                    </span>
                  </span>
                </td>
                <td className="relatorio__num">{pessoa.quantidade}</td>
                <td className="relatorio__num">
                  {dinheiro(pessoa.ticket_medio_micros, dados.moeda)}
                </td>
                <td>
                  <Variacao pessoa={pessoa} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SemVendas({ dados }: { dados: RankingDeVendas }): ReactNode {
  return (
    <section className="relatorio__grupo" aria-labelledby="sem-vendas-titulo">
      <h2 id="sem-vendas-titulo" className="relatorio__subtitulo">
        Sem vendas no período
      </h2>
      <ul className="relatorio__sem-vendas">
        {dados.sem_vendas.map((pessoa) => (
          <li key={pessoa.responsavel_referencia_externa}>
            <Avatar nome={pessoa.nome ?? '?'} tamanho={28} foto={pessoa.foto_url} />
            <span>{pessoa.nome ?? 'Sem nome'}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VendasDaPessoa({
  pessoa,
  moeda,
  filtro,
  aoFechar,
}: {
  pessoa: PosicaoNoRanking;
  moeda: string;
  filtro: Record<string, string>;
  aoFechar: () => void;
}): ReactNode {
  const consulta = useVendasDoRanking({
    ...filtro,
    responsavel: pessoa.responsavel_referencia_externa,
  });
  const quando = (instante: string) =>
    new Date(instante).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  return (
    <Dialogo
      titulo={`Vendas de ${pessoa.nome ?? 'responsável sem nome'}`}
      aoFechar={aoFechar}
      largura="larga"
    >
      <p className="relatorio__resumo">
        {dinheiro(pessoa.valor_micros, moeda)} em {vendasEmPalavras(pessoa.quantidade)}
      </p>
      <Estado consulta={consulta} forma="lista" linhas={4}>
        {(dados) => (
          <ul className="relatorio__vendas">
            {dados.itens.map((venda) => (
              <li key={venda.referencia_externa}>
                <span className="relatorio__venda-titulo">{venda.titulo ?? 'Sem título'}</span>
                <span className="relatorio__venda-meta">
                  {quando(venda.ganha_em)} · {venda.funil_nome}
                </span>
                <span className="relatorio__num">
                  {venda.valor_micros === null
                    ? 'sem valor'
                    : dinheiro(venda.valor_micros, venda.moeda ?? moeda)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Estado>
    </Dialogo>
  );
}
